import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import {
  ApplicantDetailsPreviewROWResponse,
  Question,
} from "../application-preview/types";
import { ApplicantDetailsROWResponse } from "../application-details/types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType } from "@store/store";
import { useNavigate } from "react-router-dom";
import { setActiveStep } from "@modules/stepper-row/redux/stepperROWSlice";
import {
  sectionsIfOFC,
  sectionsNoOFC,
} from "@modules/stepper-row/components/rowStaticData";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";

interface StaticAccessOption {
  id: number;
  name: string;
}

interface StaticAccessQuestion {
  title: string;
  name: string;
  type: "select" | "text" | "radio";
  options?: StaticAccessOption[] | string[];
  dataType?: string;
  nestedFields?: any[];
}

interface StaticAccessSection {
  title: string;
  fields: StaticAccessQuestion[];
}

interface PreviewQuestion {
  question_name: string;
  value: any;
  key: string;
}

type ResponseType = Record<string, any>;

export const SiteRoadwaysSection: React.FC<{
  isExpanded: boolean;
  toggleExpand: () => void;
  visible?: boolean;
  data: ApplicantDetailsPreviewROWResponse | ApplicantDetailsROWResponse;
}> = ({ isExpanded, toggleExpand, data, visible = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { amenity } = useSelector(
    (state: RootReducerType) => state.amenitiesROW,
  );

  if (!data) return null;

  // Helper function to recursively extract all fields including nested ones
  const extractAllFields = (
    sections: StaticAccessSection[],
  ): StaticAccessQuestion[] => {
    const allFields: StaticAccessQuestion[] = [];

    const extractFieldsRecursive = (fields: StaticAccessQuestion[]) => {
      fields.forEach((field) => {
        // Add the current field
        allFields.push(field);

        // Recursively extract nested fields
        if (field.nestedFields && field.nestedFields.length > 0) {
          extractFieldsRecursive(field.nestedFields);
        }
      });
    };

    sections.forEach((section) => {
      if (section.fields && section.fields.length > 0) {
        extractFieldsRecursive(section.fields);
      }
    });

    return allFields;
  };

  const buildQuestionsForPreview = (
    staticAccessData: StaticAccessSection[],
    response: ResponseType,
  ): PreviewQuestion[] => {
    // First, extract all fields (including nested ones) into a flat array
    const allFields = extractAllFields(staticAccessData);

    return allFields
      .filter(
        (q) =>
          response[q.name] !== undefined &&
          response[q.name] !== null &&
          response[q.name] !== "" &&
          response[q.name] !== "0",
      )
      .map((q) => {
        // For select, get the display value (the 'name') from the option
        if (q.type === "select") {
          const option = (q.options as StaticAccessOption[])?.find((opt) =>
            typeof response[q.name] === "number"
              ? opt.id === response[q.name]
              : opt.name === response[q.name],
          );
          return {
            question_name: q.title,
            value: option ? option.name : response[q.name],
            key: q.name,
          };
        }
        // For radio buttons that have Yes/No options
        if (q.type === "radio" && Array.isArray(q.options)) {
          const value = response[q.name];
          if (typeof value === "boolean") {
            return {
              question_name: q.title,
              value: value ? "Yes" : "No",
              key: q.name,
            };
          }
        }
        // For text, number, etc., just use the value
        return {
          question_name: q.title,
          value: response[q.name],
          key: q.name,
        };
      });
  };

  const displayValue = (val: any) => {
    if (val === true) return "Yes";
    if (val === false) return "No";
    if (val === "" || val === null || val === undefined) return "NA";
    return val.toString();
  };

  const sections =
    amenity.toLocaleLowerCase() === "ofc" ||
    amenity.toLocaleLowerCase().includes("ofc")
      ? sectionsIfOFC
      : sectionsNoOFC;

  // Build questions from static data
  const staticQuestions = buildQuestionsForPreview(sections, data);

  // Handle dynamic questions from API response
  const dynamicQuestions: PreviewQuestion[] = Array.isArray(data.questions)
    ? data.questions
        .filter(
          (q: Question) =>
            q.value !== "" && q.value !== null && q.value !== undefined,
        )
        .map((q: Question) => ({
          question_name: q.question_name,
          value: q.value,
          key: `dynamic_${q.question_name}`,
        }))
    : [];

  // Combine both static and dynamic questions
  const allQuestions = [...staticQuestions, ...dynamicQuestions];

  // Pair questions for display (2 per row)
  const questionPairs: [PreviewQuestion?, PreviewQuestion?][] = [];
  for (let i = 0; i < allQuestions.length; i += 2) {
    questionPairs.push([allQuestions[i], allQuestions[i + 1]]);
  }

  return (
    <Box>
      <Card sx={{ boxShadow: "none", border: "1px solid #E7E8E9" }}>
        <Box
          sx={{
            backgroundColor: "#F4F2F7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pl: 2,
            borderBottom: "1px solid #E7E8E9",
            py: "5px",
          }}
        >
          <Typography
            sx={{ fontSize: 12 }}
            variant="h6"
            fontWeight="semi-bold"
            className="preview-dropdown-header"
          >
            Site & Highways Information{" "}
            {visible && (
              <IconButton
                className="ml-4"
                onClick={() => {
                  dispatch(setActiveStep(1));
                  dispatch(resetApplicationSubmitted());
                  navigate("/stepper-row");
                }}
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            )}
          </Typography>
          <IconButton aria-label="Collapse section" onClick={toggleExpand}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Content */}
        {isExpanded && (
          <CardContent>
            <Box>
              {questionPairs.length > 0 ? (
                questionPairs.map((pair, idx) => (
                  <Grid container spacing={2} key={idx} sx={{ py: 1 }}>
                    {/* First question in pair */}
                    <Grid item xs={12} md={6}>
                      {pair[0] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            className="preview-dropdown-label"
                          >
                            {pair[0].question_name?.trim() || "—"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="preview-dropdown-value"
                          >
                            {displayValue(pair[0].value)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>

                    {/* Second question in pair */}
                    <Grid item xs={12} md={6}>
                      {pair[1] && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            className="preview-dropdown-label"
                          >
                            {pair[1].question_name?.trim()}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="preview-dropdown-value"
                          >
                            {displayValue(pair[1].value)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No site and roadway information available.
                </Typography>
              )}
            </Box>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};
